import * as React from "react"
import type { LucideIcon } from "lucide-react"
import { FileCheck2 } from "lucide-react"
import { useRouterState, useNavigate } from "@tanstack/react-router"
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar"
import { ErrorAndWarningBadges } from "./ErrorAndWarningBadge"

export interface NavItem {
  title: string
  icon?: LucideIcon
  url?: string
  disabled?: boolean
  badgeCount?: number
  step?: number
  badge?: "counter" | string
  action?: string
}

type ValidationBadgeConfig = {
  amountsErrors: number;
}

export interface SidebarMenuItemProps {
  item: NavItem
  isActive?: boolean
  isCollapsed?: boolean
  onStepClick?: () => void // Callback for step navigation
  documentCount?: { completed: number; total: number } // For document counter badge
  validationBadge?: ValidationBadgeConfig;
}

export function SidebarMenuItemComponent({
  item,
  isActive = false,
  isCollapsed = false,
  onStepClick,
  documentCount,
  validationBadge
}: SidebarMenuItemProps) {
  let hasRouter = true
  let navigate: ReturnType<typeof useNavigate> | null = null

  try {
    useRouterState()
    navigate = useNavigate()
  } catch {
    hasRouter = false
  }
  

  const AmountsValidationBadge = () => {
    if(item.step !== 4) return <></>
    if(!!validationBadge?.amountsErrors) {
      return (
        <span
          className={`ml-auto hidden h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-medium leading-none text-white ${!isCollapsed ? 'md:flex' : ''}`}
          aria-label={`${item.badgeCount} notifications`}
        >
          {validationBadge?.amountsErrors}
        </span>
      )
    }

    return <></>
  }

  const content = (
    <>
      {item.icon && <item.icon className="h-4 w-4 shrink-0" />}
      <span className={`hidden flex-1 min-w-0 overflow-hidden whitespace-nowrap text-ellipsis ${!isCollapsed ? 'md:inline-block' : ''}`}>{item.title}</span>

      {documentCount && (
        <span
          className={`ml-auto hidden items-center justify-center gap-1.5 rounded-lg bg-[#262626] px-2 py-0.5 min-h-6 ${!isCollapsed ? 'md:flex' : ''}`}
          aria-label={`${documentCount.completed} of ${documentCount.total} documents`}
        >
          <div className="max-w-[140px] flex flex-col justify-center text-center text-xs font-semibold leading-[18px] tracking-[0.18px] text-[#F5F5F5] whitespace-nowrap overflow-hidden text-ellipsis">
            {documentCount.completed}/{documentCount.total}
          </div>
          <div className="w-[14.49px] h-[14.49px] relative overflow-hidden shrink-0">
            <FileCheck2 className="w-full h-full" />
          </div>
        </span>
      )}

      {!documentCount && item.badgeCount !== undefined && item.badgeCount > 0 && (
        <span
          className={`ml-auto hidden h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-medium leading-none text-white ${!isCollapsed ? 'md:flex' : ''}`}
          aria-label={`${item.badgeCount} notifications`}
        >
          {item.badgeCount > 99 ? '99+' : item.badgeCount}
        </span>
      )}

      {!!item.step && <ErrorAndWarningBadges currentStep={item.step} /> }
      <AmountsValidationBadge />
    </>
  )

  const handleClick = (e: React.MouseEvent) => {
    if (item.disabled) return

    if (onStepClick) {
      e.preventDefault()
      onStepClick()
    } else if (hasRouter && navigate && item.url) {
      e.preventDefault()
      const url = item.action ? `${item.url}?${item.action}=1` : item.url
      navigate({ to: url })
    } else if (item.url) {
      window.location.href = item.url
    }
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild={false}
        isActive={isActive}
        disabled={item.disabled}
        onClick={handleClick}
        className="w-full h-8 rounded-md px-3 py-1 text-sm font-normal leading-[21px] tracking-[0.07px] text-text-subtle data-[active=true]:bg-bg-raised data-[active=true]:text-text-subtle hover:bg-bg-raised hover:text-text-subtle active:bg-bg-raised active:text-text-subtle data-[active=true]:hover:bg-bg-raised data-[active=true]:hover:text-text-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-text-subtle focus-visible:ring-offset-2 focus-visible:ring-offset-bg-subtle data-[active=true]:focus-visible:bg-bg-raised transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer justify-start gap-2"
      >
        {content}
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}
